import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, CheckCircle, Clock, Edit2, Trash2 } from 'lucide-react';
import { getTaskDuration, pconf, sconf } from '../../constants/appConstants';
import type { Task } from '../../hooks/useTasksController';
import type { ColorTokens } from '../../types/appShared';

// ══════════════════════════════════════════════
// TaskRow — صف واحد في جدول التاسكات، مفصول كـ كومبوننت مستقل ومغلّف بـ
// React.memo. يعني: لو أي state تاني في App اتغيّر (زي كتابة حرف في خانة بحث
// في صفحة تانية، أو فتح مودال)، الصفوف مش هترندر نفسها تاني — غير
// الصف اللي فعلاً اتغيّرت بياناته أو حالة تحديده (isSelected) بس.
// ══════════════════════════════════════════════
interface TaskRowProps {
    task: Task;
    index: number;
    darkMode: boolean;
    C: ColorTokens;
    isSelected: boolean;
    openChallengesCount: number;
    onToggleSelect: (taskId: string | number, checked: boolean) => void;
    onOpenDetails: (task: Task) => void;
    onEdit: (task: Task) => void;
    onDelete: (taskId: string | number) => void;
    onValueChange?: (taskId: string | number, value: number) => void;
    totalTaskValue: number;
    onConfirmCompletion?: (taskId: string | number) => void;
}

const TaskRow = React.memo(function TaskRow({
    task, index, darkMode, C, isSelected, openChallengesCount,
    onToggleSelect, onOpenDetails, onEdit, onDelete, onValueChange, totalTaskValue,
    onConfirmCompletion,
}: TaskRowProps) {
    // ✅ قيمة التاسك (SAR) — قيمة محلية عشان الكتابة تكون سلسة، وبتتزامن مع
    // القيمة الحقيقية (task.task_value) لو اتغيّرت من مصدر تاني (زي تعديل من المودال).
    // الـ Weight% مش عمود بيتعدّل — بيتحسب لايف من (task_value ÷ إجمالي قيم المشروع).
    const [localValue, setLocalValue] = React.useState<number | string>(task.task_value ?? 0);
    React.useEffect(() => { setLocalValue(task.task_value ?? 0); }, [task.task_value]);

    const commitValue = () => {
        const num = parseFloat(String(localValue));
        const finalVal = isNaN(num) || num < 0 ? 0 : num;
        setLocalValue(finalVal);
        if (finalVal !== (task.task_value ?? 0) && typeof onValueChange === 'function') {
            onValueChange(task.id, finalVal);
        }
    };
    const weightPct = totalTaskValue > 0 ? ((parseFloat(String(task.task_value)) || 0) / totalTaskValue) * 100 : 0;

    const today = new Date();
    const deadline = task.deadline_date ? new Date(task.deadline_date) : null;
    const daysLeft = deadline ? Math.ceil((deadline.getTime() - today.getTime()) / 86400000) : null;
    const isOverdue = !!(deadline && daysLeft !== null && daysLeft < 0 && task.status !== 'Completed');
    const isUrgent = !!(deadline && daysLeft !== null && daysLeft >= 0 && daysLeft <= 3 && task.status !== 'Completed');
    const duration = getTaskDuration(task.start_date, task.deadline_date);
    const sc = sconf(task.status);
    const pc = pconf(task.priority || 'Medium');

    return (
        <motion.tr
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(index, 30) * 0.015 }}
            onClick={() => onOpenDetails(task)}
            style={{
                borderBottom: `1px solid ${C.border}`,
                background: isSelected ? (darkMode ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.04)') : isOverdue ? (darkMode ? 'rgba(239,68,68,0.04)' : 'rgba(239,68,68,0.02)') : 'transparent',
                cursor: 'pointer',
                transition: 'background 0.1s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'}
            onMouseLeave={e => e.currentTarget.style.background = isSelected ? (darkMode ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.04)') : isOverdue ? (darkMode ? 'rgba(239,68,68,0.04)' : 'rgba(239,68,68,0.02)') : 'transparent'}
        >
            {/* Checkbox */}
            <td style={{ padding: '11px 14px' }} onClick={e => e.stopPropagation()}>
                <input type="checkbox" checked={isSelected}
                    aria-label={`Select task: ${task.sub_task || 'Unnamed Task'}`}
                    onChange={e => onToggleSelect(task.id, e.target.checked)}
                    style={{ width: 14, height: 14, cursor: 'pointer' }} />
            </td>

            {/* Priority */}
            <td style={{ padding: '11px 14px' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, background: pc.bg, border: `1px solid ${pc.border}`, color: pc.color, fontSize: 10, fontWeight: 800 }}>
                    {(task.priority || 'Medium')}
                </span>
            </td>

            {/* Task name */}
            <td style={{ padding: '11px 14px', maxWidth: 280 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>{task.sub_task || 'Unnamed Task'}</span>
                    {openChallengesCount > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 99, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', fontSize: 9, fontWeight: 900, flexShrink: 0 }}>
                            <AlertCircle size={9} />{openChallengesCount}
                        </span>
                    )}
                </div>
                {task.notes && <p style={{ fontSize: 10, color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 230 }}>{task.notes}</p>}
            </td>

            {/* System */}
            <td style={{ padding: '11px 14px' }}>
                <span style={{ fontSize: 11, color: C.sub, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140, display: 'block' }}>
                    {task.system || '—'}
                </span>
            </td>

            {/* Assignee */}
            <td style={{ padding: '11px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    {/* ✅ لون موحّد بدل التدرج indigo→violet — نفس لون أفاتار الموظف
                        في كارد الـ Kanban (TasksView.jsx) عشان الاتساق بين الـ Table والـ Board */}
                    <div style={{ width: 26, height: 26, borderRadius: 8, background: '#1D4ED8', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 900, flexShrink: 0 }}>
                        {(task.assigned_to || 'U').charAt(0).toUpperCase()}
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>{task.assigned_to || 'Unassigned'}</span>
                </div>
            </td>

            {/* Dates */}
            <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                {deadline ? (
                    <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{deadline.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</div>
                        {isOverdue ? (
                            <span style={{ fontSize: 9, fontWeight: 800, color: '#ef4444', background: 'rgba(239,68,68,0.1)', padding: '1px 5px', borderRadius: 4 }}>{Math.abs(daysLeft as number)}d overdue</span>
                        ) : isUrgent ? (
                            <span style={{ fontSize: 9, fontWeight: 800, color: '#f97316', background: 'rgba(249,115,22,0.1)', padding: '1px 5px', borderRadius: 4 }}>{daysLeft}d left</span>
                        ) : daysLeft !== null ? (
                            <span style={{ fontSize: 9, color: C.sub, fontWeight: 600 }}>{daysLeft}d remaining</span>
                        ) : null}
                        {duration && <div style={{ fontSize: 9, color: C.sub, marginTop: 1 }}>{duration}d duration</div>}
                    </div>
                ) : <span style={{ fontSize: 11, color: C.sub }}>—</span>}
            </td>

            {/* Progress */}
            <td style={{ padding: '11px 14px', minWidth: 120 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, height: 4, background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', borderRadius: 99, overflow: 'hidden' }}>
                        <motion.div initial={{ width: 0 }} animate={{ width: `${task.progress || 0}%` }} transition={{ duration: 0.8, delay: Math.min(index, 30) * 0.01 }}
                            style={{ height: '100%', borderRadius: 99, background: (task.progress || 0) === 100 ? '#10b981' : (task.progress || 0) >= 60 ? '#3b82f6' : '#f59e0b' }} />
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 900, color: C.text, minWidth: 28, textAlign: 'right' }}>{task.progress || 0}%</span>
                </div>
            </td>

            {/* Task Value — قابل للتعديل مباشرة من الجدول (بدل جدول Supabase)؛
                الـ Weight% تحته بيتحسب لايف، مش عمود منفصل بيتعدّل. */}
            <td style={{ padding: '11px 14px' }} onClick={e => e.stopPropagation()}>
                <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={localValue}
                    onChange={e => setLocalValue(e.target.value)}
                    onBlur={commitValue}
                    onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    style={{
                        width: 78, padding: '5px 7px', borderRadius: 7,
                        border: `1px solid ${C.border}`, background: C.input, color: C.text,
                        fontSize: 11, fontWeight: 800, textAlign: 'center', outline: 'none',
                    }}
                />
                <div style={{ fontSize: 9, color: C.sub, fontWeight: 700, textAlign: 'center', marginTop: 2 }}>{weightPct.toFixed(1)}%</div>
            </td>

            {/* Status */}
            <td style={{ padding: '11px 14px' }} onClick={e => task.completion_pending_confirmation && e.stopPropagation()}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 8, background: sc.bg, border: `1px solid ${sc.border}`, color: sc.color, fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap' }}>
                    {task.status === 'Completed' ? <CheckCircle size={11} /> : task.status === 'In Progress' ? <Clock size={11} /> : <AlertCircle size={11} />}
                    {task.status}
                </span>
                {task.completion_pending_confirmation && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onConfirmCompletion && onConfirmCompletion(task.id); }}
                        aria-label={`Confirm completion for task: ${task.sub_task || 'Unnamed Task'}`}
                        title="Reached 100% — confirm completion"
                        style={{
                            display: 'flex', alignItems: 'center', gap: 4, marginTop: 5,
                            padding: '3px 8px', borderRadius: 7, cursor: 'pointer',
                            background: 'rgba(21,128,61,0.1)', border: '1px solid rgba(21,128,61,0.3)',
                            color: '#15803D', fontSize: 9.5, fontWeight: 800, whiteSpace: 'nowrap',
                        }}>
                        <CheckCircle size={10} /> Confirm 100%
                    </button>
                )}
            </td>

            {/* Actions */}
            <td style={{ padding: '11px 14px' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', gap: 5 }}>
                    <button onClick={() => onEdit(task)}
                        aria-label={`Edit task: ${task.sub_task || 'Unnamed Task'}`}
                        style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#3b82f6' }}>
                        <Edit2 size={12} />
                    </button>
                    <button onClick={() => onDelete(task.id)}
                        aria-label={`Delete task: ${task.sub_task || 'Unnamed Task'}`}
                        style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#ef4444' }}>
                        <Trash2 size={12} />
                    </button>
                </div>
            </td>
        </motion.tr>
    );
});

export default TaskRow;
